import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Mail, Lock, Loader2, LogIn } from "lucide-react";
import { useCustomerAuth } from "../context/CustomerAuthContext";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SignIn() {
  const { login, googleLogin } = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Cynos Sign In";
  }, []);

  const from = location.state?.from || "/profile";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email.trim()) {
      setError("Email is required.");
      return;
    }
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!password) {
      setError("Password is required.");
      return;
    }

    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate(from, { replace: true });
    } catch (err) {
      const message = err.response?.data?.detail || err.message || "Unable to sign in.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    setLoading(true);
    try {
      await googleLogin(from);
    } catch (err) {
      const message = err.response?.data?.detail || err.message || "Google login failed. Please try again.";
      setError(message);
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-black px-4 py-24 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="mx-auto max-w-md rounded-3xl border border-white/10 bg-cynos-card p-8 shadow-2xl shadow-black/20"
      >
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center rounded-full bg-cynos-red/10 p-4 mb-4">
            <LogIn className="h-6 w-6 text-cynos-red" />
          </div>
          <h1 className="text-3xl font-anton uppercase text-white">Sign In</h1>
          <p className="mt-3 text-sm text-zinc-400">Access your Cynos account for order tracking and faster checkout.</p>
        </div>

        {error && <div className="mb-6 rounded-2xl bg-red-500/15 px-4 py-3 text-sm text-red-200">{error}</div>}

        <button
          type="button"
          onClick={handleGoogle}
          disabled={loading}
          className="mb-6 flex w-full items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm uppercase tracking-[0.2em] text-white transition hover:border-cynos-red/30 hover:bg-white/10 disabled:cursor-not-allowed"
        >
          <Mail className="h-4 w-4" />
          Continue with Gmail
        </button>

        <div className="mb-6 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-zinc-500">
          <span className="h-px flex-1 bg-white/10" />
          or use email
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-zinc-400">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-2xl border border-white/10 bg-black/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cynos-red"
              placeholder="customer@example.com"
            />
          </div>
          <div>
            <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-zinc-400">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-2xl border border-white/10 bg-black/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cynos-red"
              placeholder="Enter your password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-cynos-red px-4 py-3 text-sm uppercase tracking-[0.2em] text-black transition hover:bg-cynos-red/90 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? "Signing In" : "Sign In"}
          </button>
        </form>

        <div className="mt-6 flex flex-col gap-3 text-sm text-zinc-400">
          <Link to="/forgot-password" className="underline hover:text-white">Forgot Password?</Link>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>New to Cynos?</span>
            <Link to="/signup" className="text-white hover:text-cynos-red font-semibold">Create Account</Link>
          </div>
        </div>
      </motion.div>
    </main>
  );
}
